import { useState, useEffect } from 'react';
import axios from 'axios';
import { Factory, Plus, Pencil, Trash2, Save, X, ToggleLeft, ToggleRight, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlantManagement = () => {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, plant: null });
  const [newPlant, setNewPlant] = useState({ name: '', code: '', location: '' });
  const [editFormData, setEditFormData] = useState({ name: '', code: '', location: '' });

  useEffect(() => {
    fetchPlants();
  }, []);

  const fetchPlants = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/plants`);
      setPlants(response.data);
    } catch (error) {
      toast.error('Failed to load plants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPlant.name || !newPlant.code) {
      toast.error('Plant name and code are required');
      return;
    }

    const duplicate = plants.find(p => p.code === newPlant.code);
    if (duplicate) {
      toast.error(`Plant code '${newPlant.code}' already exists`);
      return;
    }

    try {
      await axios.post(`${API}/plants`, {
        name: newPlant.name,
        code: newPlant.code,
        location: newPlant.location || `${newPlant.name} Location`,
        is_active: true
      });
      toast.success(`Plant "${newPlant.name}" created successfully`);
      setNewPlant({ name: '', code: '', location: '' });
      setShowCreateDialog(false);
      fetchPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plant');
    }
  };

  const handleEditClick = (plant) => {
    setEditDialog({ open: true, plant });
    setEditFormData({
      name: plant.name,
      code: plant.code,
      location: plant.location || ''
    });
  };

  const handleEditSave = async () => {
    if (!editFormData.name || !editFormData.code) {
      toast.error('Plant name and code are required');
      return;
    }

    try {
      await axios.put(`${API}/plants/${editDialog.plant.id}`, {
        name: editFormData.name,
        code: editFormData.code,
        location: editFormData.location || `${editFormData.name} Location`,
        is_active: editDialog.plant.is_active
      });
      toast.success(`Plant "${editFormData.name}" updated successfully`);
      setEditDialog({ open: false, plant: null });
      fetchPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plant');
    }
  };

  const handleToggleActive = async (plant) => {
    try {
      const result = await axios.patch(`${API}/plants/${plant.id}/toggle-active`);
      toast.success(result.data.message);
      fetchPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle plant status');
    }
  };

  const handleDelete = async (plant) => {
    if (!window.confirm(`Are you sure you want to delete plant "${plant.name} (${plant.code})"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/plants/${plant.id}`);
      toast.success(`Plant "${plant.name}" deleted`);
      fetchPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plant');
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="plant-management-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-slate-700" />
              <div>
                <CardTitle>Plant Management</CardTitle>
                <CardDescription>Manage manufacturing plants and locations</CardDescription>
              </div>
            </div>
            <Button
              data-testid="add-plant-btn"
              onClick={() => setShowCreateDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Plant
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading plants...</div>
          ) : plants.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No plants configured. Click "Add Plant" to create one.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="h-10 px-4 text-left font-medium">Code</th>
                    <th className="h-10 px-4 text-left font-medium">Plant Name</th>
                    <th className="h-10 px-4 text-left font-medium">Location</th>
                    <th className="h-10 px-4 text-left font-medium">Status</th>
                    <th className="h-10 px-4 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plants.map(plant => (
                    <tr key={plant.id} data-testid={`plant-row-${plant.id}`} className="border-b hover:bg-slate-50 transition-colors">
                      <td className="p-4 font-mono font-semibold text-blue-700">{plant.code}</td>
                      <td className="p-4 font-medium">{plant.name}</td>
                      <td className="p-4 text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          {plant.location || '-'}
                        </div>
                      </td>
                      <td className="p-4">
                        {plant.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEditClick(plant)}
                            title="Edit plant"
                            data-testid={`edit-plant-${plant.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={plant.is_active ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}
                            onClick={() => handleToggleActive(plant)}
                            title={plant.is_active ? "Deactivate" : "Activate"}
                            data-testid={`toggle-plant-${plant.id}`}
                          >
                            {plant.is_active ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(plant)}
                            title="Delete plant"
                            data-testid={`delete-plant-${plant.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Plant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Plant</DialogTitle>
            <DialogDescription>Create a new manufacturing plant</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Plant Code *</label>
              <Input
                data-testid="input-plant-code"
                placeholder="e.g. 1401"
                value={newPlant.code}
                onChange={(e) => setNewPlant({ ...newPlant, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Plant Name *</label>
              <Input
                data-testid="input-plant-name"
                placeholder="e.g. Hosur"
                value={newPlant.name}
                onChange={(e) => setNewPlant({ ...newPlant, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                data-testid="input-plant-location"
                placeholder="e.g. Hosur, Tamil Nadu"
                value={newPlant.location}
                onChange={(e) => setNewPlant({ ...newPlant, location: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button data-testid="create-plant-submit" onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Plant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plant Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, plant: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Plant</DialogTitle>
            <DialogDescription>Update plant details</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Plant Code *</label>
              <Input
                data-testid="edit-plant-code"
                value={editFormData.code}
                onChange={(e) => setEditFormData({ ...editFormData, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Plant Name *</label>
              <Input
                data-testid="edit-plant-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                data-testid="edit-plant-location"
                value={editFormData.location}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, plant: null })}>Cancel</Button>
            <Button data-testid="save-plant-edit" onClick={handleEditSave} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlantManagement;
